import React, { FC } from 'react';
import { Controller, Control, UseFormSetValue } from 'react-hook-form';
import type { PatientInfoValues } from '../types/flow';
import {
  FormInput, FormSelect, FLabel, SectionDivider,
} from '../../assessment-portal/components/form-fields';
import {
  MEDICARE_TYPE_OPTIONS,
  DVA_CARD_TYPES,
  RELATIONSHIPS,
} from '../data/constants';
import { AU_MOBILE_PATTERN } from '../utils/details-validation';

interface EmergencyMedicareFormProps {
  control: Control<PatientInfoValues>;
  values: Record<string, any>;
  setValue: UseFormSetValue<PatientInfoValues>;
}

const EmergencyMedicareForm: FC<EmergencyMedicareFormProps> = ({
  control,
  values,
  setValue,
}) => (
  <>
    <h2 className="text-xl font-bold mb-1 text-brand-dark">Emergency &amp; Medicare</h2>
    <p className="text-slate-500 text-sm mb-5">Step 2 of 2</p>

    <div className="space-y-4">
      <SectionDivider>Emergency Contact</SectionDivider>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <FLabel isRequired>Full Name</FLabel>
          <Controller
            name="emergencyName"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Full name" />}
          />
        </div>
        <div>
          <FLabel isRequired>Relationship</FLabel>
          <Controller
            name="emergencyRelationship"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => (
              <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Select...">
                {RELATIONSHIPS.map(({ value: val, label }) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </FormSelect>
            )}
          />
        </div>
      </div>
      <div>
        <FLabel isRequired>Phone</FLabel>
        <Controller
          name="emergencyMobile"
          control={control}
          rules={{
            required: 'Phone number is required',
            pattern: { value: AU_MOBILE_PATTERN, message: 'Please enter a valid Australian mobile (04XXXXXXXX)' },
          }}
          render={({
            field: {
              ref, name, value, onChange, onBlur,
            },
            fieldState: { error },
          }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="04XX XXX XXX" inputMode="tel" autoComplete="tel" error={error?.message} />}
        />
      </div>

      <SectionDivider>Medicare / DVA</SectionDivider>
      <div>
        <FLabel isRequired>Do you have a Medicare or DVA card?</FLabel>
        <div className="flex gap-2 flex-wrap">
          {MEDICARE_TYPE_OPTIONS.map(({ value: val, label }) => {
            const selected = values.medicareType === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => setValue('medicareType', val as any)}
                className={`flex-1 py-2.5 px-3 rounded-xl border-2 text-xs font-semibold transition-all ${
                  selected
                    ? 'border-brand bg-brand bg-opacity-5 text-brand'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {values.medicareType === 'medicare' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
          <div>
            <FLabel isRequired>Card Number</FLabel>
            <Controller
              name="medicareNumber"
              control={control}
              render={({
                field: {
                  ref, name, value, onChange, onBlur,
                },
              }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="XXXX XXXXX X" inputMode="numeric" />}
            />
          </div>
          <div>
            <FLabel isRequired>Ref No.</FLabel>
            <Controller
              name="medicareRef"
              control={control}
              render={({
                field: {
                  ref, name, value, onChange, onBlur,
                },
              }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="1–9" inputMode="numeric" />}
            />
          </div>
          <div>
            <FLabel isRequired>Expiry</FLabel>
            <Controller
              name="medicareExpiry"
              control={control}
              render={({
                field: {
                  ref, name, value, onChange, onBlur,
                },
              }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="MM/YYYY" />}
            />
          </div>
        </div>
      )}

      {values.medicareType === 'dva' && (
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">🎖️</span>
            <p className="text-xs font-bold text-slate-700">DVA Card Details</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <FLabel isRequired>DVA File Number</FLabel>
              <Controller
                name="dvaNumber"
                control={control}
                render={({
                  field: {
                    ref, name, value, onChange, onBlur,
                  },
                }) => (
                  <FormInput
                    ref={ref}
                    name={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    placeholder="e.g. VX12345"
                  />
                )}
              />
            </div>
            <div>
              <FLabel isRequired>Card Colour</FLabel>
              <Controller
                name="dvaCardType"
                control={control}
                render={({
                  field: {
                    ref, name, value, onChange, onBlur,
                  },
                }) => (
                  <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur}>
                    {DVA_CARD_TYPES.map((cardType) => <option key={cardType} value={cardType}>{cardType}</option>)}
                  </FormSelect>
                )}
              />
            </div>
          </div>
          <div className="rounded-lg p-3 border text-xs leading-relaxed bg-blue-50 border-blue-200 text-blue-800">
            <strong>White card:</strong>
            {' '}
            treatment for service-related conditions only.
            <strong>Gold card:</strong>
            {' '}
            all conditions covered.
            <strong>Orange card:</strong>
            {' '}
            pharmaceutical only.
          </div>
        </div>
      )}

      {values.medicareType === 'none' && (
        <div className="rounded-xl p-3 border flex gap-2 bg-amber-50 border-amber-300">
          <span>💡</span>
          <p className="text-xs text-amber-700">
            <strong>No problem</strong>
            , without having any card — You may not be eligible for a Medicare rebate without a valid card on file, which will incur higher private fees. Please use our fee estimator to calculate your total private fee.
          </p>
        </div>
      )}
    </div>
  </>
);

export default EmergencyMedicareForm;
